import { SelectChangeEvent } from "@mui/material";
import { createContext, FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getMonthlyCreditsByType,
  getSuborganizationsByOrgId,
  getTotalCreditosUser,
} from "../../../api/broker";
import {
  BarFilters,
  CreditosMensuales,
  CreditosUsario,
  initalBarFilters,
  MonthYear,
} from "../../../interfaces/SuperAdmin/Pages/iDataCreditos";
import { DashboardType } from "../../../interfaces/SuperAdmin/Pages/iDataDashboard";
import { iDataSuborganizacion } from "../../../interfaces/SuperAdmin/Pages/iDataSuborganizacion";

interface AllUsersDashboardContextProps {
  totalCredits: CreditosUsario[];
  selectedUser: CreditosUsario | undefined;
  suborgs: iDataSuborganizacion[];
  selectedSuborg: string;
  monthlyCredits: CreditosMensuales[];

  months: number;
  startMonth: MonthYear | undefined;
  totalCreditsFilters: BarFilters;
  monthlyCreditsFilters: BarFilters;

  isLoadingSuborgs: boolean;
  isLoadingTotalCredits: boolean;
  isLoadingMonthlyCredits: boolean;

  changeMonths: (value: number) => void;
  updateStartingMonth: (month: number, year: number) => void;
  handleToggleCredit: (value: string) => void;
  handleToggleMonthlyCredit: (value: string) => void;
  changeSuborg: (event: SelectChangeEvent) => void;
}

const TODOS_ID = "123";

const AllUsersDashboardContext = createContext<AllUsersDashboardContextProps>(
  {} as AllUsersDashboardContextProps
);

interface Props {
  children: React.ReactNode;
}

const AllUsersDashboardProvider: FC<Props> = ({ children }) => {
  // credits tendrá los créditos, y el tipo dependerá de qué filtro tenga
  const [totalCredits, setTotalCredits] = useState<CreditosUsario[]>([]);
  const [selectedUser, setSelectedUser] = useState<CreditosUsario | undefined>(
    undefined
  );
  const [suborgs, setSuborgs] = useState<iDataSuborganizacion[]>([]);
  const [selectedSuborg, setSelectedSuborg] = useState(TODOS_ID);
  const [monthlyCredits, setMonthlyCredits] = useState<CreditosMensuales[]>([]);

  const [months, setMonths] = useState(12);
  const [startMonth, setStartMonth] = useState<MonthYear | undefined>(
    undefined
  );
  const [totalCreditsFilters, setTotalCreditsFilters] =
    useState<BarFilters>(initalBarFilters);
  const [monthlyCreditsFilters, setMonthlyCreditsFilters] =
    useState<BarFilters>(initalBarFilters);

  const [isLoadingSuborgs, setIsLoadingSuborgs] = useState(true);
  const [isLoadingTotalCredits, setIsLoadingTotalCredits] = useState(true);
  const [isLoadingMonthlyCredits, setIsLoadingMonthlyCredits] = useState(true);

  const { idOrganization, idUser } = useParams();

  // Fetch de creditos totales de una organización
  const fetchCreditos = async (id_org: string, id_suborg: string | null) => {
    setIsLoadingTotalCredits(true);
    try {
      const res = await getTotalCreditosUser(id_org, id_suborg);
      if (res.data.statusCode === 200) {
        setTotalCredits(res.data.body as CreditosUsario[]);
      } else {
        toast.error("Error al obtener los créditos totales");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los créditos totales");
    } finally {
      setIsLoadingTotalCredits(false);
    }
  };

  // Fetch de creditos mensuales de una suscripcion
  const fetchMonthlyCredits = async (
    idUser: string,
    startMonth: MonthYear | undefined,
    months: string
  ) => {
    setIsLoadingMonthlyCredits(true);
    try {
      // Con esta linea formateamos startMonth para que quede en el formato 'MM/YYYY'
      let firstMonth = startMonth
        ? `${startMonth.month}/${startMonth.year}`
        : undefined;

      const monthlyRes = await getMonthlyCreditsByType(
        DashboardType.USERS,
        idUser,
        firstMonth,
        months
      );
      console.log(monthlyRes);

      if (monthlyRes.data.statusCode === 200) {
        if (!startMonth) {
          const { start_month, start_year } = monthlyRes.data.body;
          setStartMonth({ year: start_year, month: start_month });
        }
        setMonthlyCredits(monthlyRes.data.body.sublimaciones);
      } else {
        toast.error("Error al obtener los créditos mensuales");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los créditos mensuales");
    } finally {
      setIsLoadingMonthlyCredits(false);
    }
  };

  // Fetch de suborganizaciones de la organización
  const fetchSuborgs = async (id: string) => {
    setIsLoadingSuborgs(true);
    try {
      const res = await getSuborganizationsByOrgId(id);
      console.log(res);
      if (res.data.statusCode === 200) {
        const newTemp: iDataSuborganizacion[] = [
          {
            id_suborganizacion: TODOS_ID,
            suborganizacion: "Todos",
          } as iDataSuborganizacion,
          ...res.data.body,
        ];
        setSuborgs(newTemp);
      } else {
        toast.error("Error al obtener las suborganizaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las suborganizaciones");
    } finally {
      setIsLoadingSuborgs(false);
    }
  };

  // Ejecutamos el fetch al montar el componente para traer suborganizaciones
  useEffect(() => {
    if (idOrganization) {
      fetchSuborgs(idOrganization);
    }
  }, [idOrganization]);

  // Fetch para traer los créditos totales
  useEffect(() => {
    if (idOrganization) {
      const suborg = selectedSuborg !== TODOS_ID ? selectedSuborg : null;
      fetchCreditos(idOrganization, suborg);
    }
  }, [idOrganization, selectedSuborg]);

  // Cada vez que cambie idUser, vamos a buscarla dentro de los usuarios ya traídos
  useEffect(() => {
    if (idUser) {
      const foundUser = totalCredits.find((susc) => susc.id_usuario === idUser);
      setSelectedUser(foundUser);

      // Fetch de creditos mensuales
      fetchMonthlyCredits(idUser, startMonth, String(months));
    } else {
      // Con este if, en caso de que no exista idUser, reiniciamos los valores de months y start_month
      setMonths(12);
      setStartMonth(undefined);
    }
  }, [idUser, months, startMonth]);

  // Funcion para cambiar la cantidad de meses
  const changeMonths = (value: number) => {
    setMonths(value);
  };

  // Funcion para actualizar el mes de inicio
  const updateStartingMonth = (month: number, year: number) => {
    setStartMonth({ month, year });
  };

  // Función para cambiar filtros
  const handleToggleCredit = (value: string) => {
    setTotalCreditsFilters((prev) => ({
      ...prev,
      credits: prev.credits.map((credit) =>
        credit.value === value
          ? { ...credit, isActive: !credit.isActive }
          : credit
      ),
    }));
  };

  // Función para cambiar filtros de mes
  const handleToggleMonthlyCredit = (value: string) => {
    setMonthlyCreditsFilters((prev) => ({
      ...prev,
      credits: prev.credits.map((credit) =>
        credit.value === value
          ? { ...credit, isActive: !credit.isActive }
          : credit
      ),
    }));
  };

  // Función para cambiar la suborganización seleccionada
  const changeSuborg = (event: SelectChangeEvent) => {
    setSelectedSuborg(event.target.value);
  };

  return (
    <AllUsersDashboardContext.Provider
      value={{
        totalCredits,
        selectedUser,
        suborgs,
        selectedSuborg,
        monthlyCredits,
        months,
        startMonth,
        totalCreditsFilters,
        monthlyCreditsFilters,
        isLoadingSuborgs,
        isLoadingTotalCredits,
        isLoadingMonthlyCredits,
        changeMonths,
        updateStartingMonth,
        handleToggleCredit,
        handleToggleMonthlyCredit,
        changeSuborg,
      }}
    >
      {children}
    </AllUsersDashboardContext.Provider>
  );
};

const useAllUsersDashboard = () => useContext(AllUsersDashboardContext);

export { AllUsersDashboardProvider, useAllUsersDashboard };
